import {
  stashAgentDraft,
  type AgentDraft,
  type AgentDraftSkill,
} from "@/shared/domain/agents";
import { RobotoNotFoundException } from "@/shared/http";
import { Goto } from "@/shared/navigation";

import { ApprovalMode } from "./approvalModes";
import {
  coerceToAgentDraft,
  normalizeSkillNames,
  previewAgentProposal,
  trimOrUndefined,
  type ClientCreateAgentInput,
} from "./clientCreateAgentCoerce";
import {
  type ClientToolDeps,
  type ClientToolExecutionResult,
  createClientTool,
  toolError,
  toolSuccess,
} from "./ClientTools";
import { CreateAgentConfirmation } from "./CreateAgentConfirmation";

// Coercion lives in `clientCreateAgentCoerce` so the approval preview can
// normalize identically without the tool ⇄ confirmation import cycle a
// definition here would create — mirroring `createSkillCoerce`. Re-exported so
// the co-located test can import the normalization contract alongside `tool`.
export {
  coerceToAgentDraft,
  previewAgentProposal,
  type ClientCreateAgentInput,
};

// ---------------------------------------------------------------------------
// JSON Schema
//
// Goals are a single flat item shape with all variant-specific fields
// optional. The LLM picks which fields to populate based on the value it
// supplies for `goal_type` ('dataset_summary', 'dataset_triage', or
// 'create_events') and `normalizeGoals` discriminates server-side
// (browser-side, here) by reading `goal_type` directly. Sibling tools
// (`navigate`, `createEvents`) follow the same flat-and-discriminate
// convention; an earlier draft used `oneOf` keyed on `goal_type` but no
// test exercises Bedrock's Converse tool-schema validator against
// `oneOf` in array items, and a rejection there would fail the entire
// thread request (not just this tool).
// ---------------------------------------------------------------------------

const inputSchema: Record<string, unknown> = {
  type: "object",
  properties: {
    // Property descriptions stay one line each: the mode-selection
    // semantics live in the tool description (what the model reads to pick
    // the tool), and every tool spec is re-sent on every thread request, so
    // restating them here is pure per-request cost.
    name: {
      type: "string",
      description:
        "Short human-readable name for the agent. Required when creating; " +
        "when updating, omit to keep the saved name or set it to rename.",
    },
    target_agent_name: {
      type: "string",
      description:
        "Exact (case-sensitive) name of an existing agent to update instead " +
        "of creating a new one. Omit to create.",
    },
    description: {
      type: "string",
      description:
        "Longer prose describing what the agent does and when teammates should invoke it.",
    },
    model_profile: {
      type: "string",
      description:
        "Optional model-profile id (e.g. 'standard', 'advanced'). Omit to use the org default.",
    },
    kickoff_message: {
      type: "string",
      description:
        "Optional user message seeded as the agent's first turn. Reference variables with {{name}}.",
    },
    goals: {
      type: "array",
      description:
        "Goals the agent must accomplish on each invocation. At least one goal or a kickoff_message is required for the agent to do anything; otherwise the editor surfaces a 'needs a goal or kickoff' warning.",
      items: {
        type: "object",
        description:
          "One goal. Set `goal_type` to 'dataset_summary' (then optionally `summary_format_spec_prompt`), 'dataset_triage' (then provide `label_vocabulary`), or 'create_events' (then provide `event_vocabulary` and optionally `tag_vocabulary`, `collection_enabled`, `event_focus_prompt`). Other fields are ignored for the chosen type.",
        properties: {
          goal_type: {
            type: "string",
            enum: ["dataset_summary", "dataset_triage", "create_events"],
            description:
              "Which goal variant. `dataset_summary` makes the agent write a summary of the dataset; `dataset_triage` makes it apply zero or more labels from a vocabulary; `create_events` makes the agent investigate the dataset and file events drawn from a fixed event vocabulary, optionally tagged from a fixed tag vocabulary.",
          },
          summary_format_spec_prompt: {
            type: "string",
            description:
              "For `dataset_summary` only: optional natural-language description of what the summary should include / emphasize.",
          },
          label_vocabulary: {
            type: "object",
            description:
              "For `dataset_triage` only: a map from label name to a description that tells the agent when to apply it. The agent picks one label per dataset.",
            additionalProperties: { type: "string" },
          },
          event_vocabulary: {
            type: "object",
            description:
              "For `create_events` only (required): map of event-type name to a description of what that event captures. The agent may only create events whose `name` is one of these keys.",
            additionalProperties: { type: "string" },
          },
          tag_vocabulary: {
            type: "object",
            description:
              "For `create_events` only (optional): map of tag name to a description of when it applies. The agent picks a subset to attach to each created event. Omit or leave empty to create untagged events.",
            additionalProperties: { type: "string" },
          },
          collection_enabled: {
            type: "boolean",
            description:
              "For `create_events` only (optional). When true, the launch page prompts the caller to pick an event collection and every created event is added to it. When false/omitted, events are created on the dataset but not filed into any collection.",
          },
          event_focus_prompt: {
            type: "string",
            description:
              "For `create_events` only: optional natural-language guidance layered on top of the vocabularies (e.g. 'only flag intervals longer than five seconds').",
          },
        },
        required: ["goal_type"],
      },
    },
    skills: {
      type: "array",
      items: { type: "string" },
      description:
        "Names of existing org-shared Skills the agent may invoke while it runs, exactly as they appear in the skill registry (without any '(private)' / '(org)' suffix). Attach a skill instead of pasting its procedure into kickoff_message — the agent loads the skill's current version at run time, so the procedure stays versioned and editable in one place. Private skills cannot be attached. Replaces the whole set when provided; pass [] to detach all, or omit to leave an existing agent's skills unchanged.",
    },
    variables: {
      type: "array",
      description:
        "Variables referenced via {{name}} placeholders in kickoff_message or goal text. Only declare variables you actually reference; extras are dropped silently by the editor.",
      items: {
        type: "object",
        properties: {
          name: {
            type: "string",
            description:
              "Variable name matching ^[a-zA-Z_][a-zA-Z0-9_.]*$. Used inside {{...}}.",
          },
          description: {
            type: "string",
            description:
              "Human-readable explanation shown next to the variable's input control at invoke time.",
          },
          default: {
            type: "string",
            description:
              "Default value substituted when the caller omits the variable.",
          },
        },
        required: ["name"],
      },
    },
  },
  // No top-level `required` key: update-mode (target_agent_name set) allows
  // `name` to be omitted to keep the saved agent's name, and a schema
  // `required` can't express "required only when target_agent_name is
  // absent" without a conditional the Bedrock Converse validator may reject.
  // Create-mode still requires a non-empty name, enforced in `execute`.
  //
  // The key is omitted rather than set to `[]` because every sibling tool
  // here ships a non-empty `required`, leaving an empty one untested against
  // Converse's validator — and a rejection there fails the whole thread
  // request, not just this tool. Omitting the key is unambiguously valid in
  // every JSON Schema draft, so it needs no such bet.
};

// ---------------------------------------------------------------------------
// Execute
//
// Two modes, discriminated by `target_agent_name`. Neither persists — the
// editor is the save seam in both. A failed update returns a `toolError` so
// the model can relay it; we never fall back to the create route or open a
// dead page.
// ---------------------------------------------------------------------------

/**
 * Update handoff: resolve the target name to an agent id, stash a draft
 * carrying that id plus the supplied fields, and open that agent's editor.
 */
/**
 * Resolve skill names to the `{skill_id}` entries the draft carries, or
 * return a `toolError` naming the first one that can't be attached.
 *
 * Resolution happens here rather than in the editor for the same reason the
 * agent lookup does: a name the model invented has to come back as an error
 * it can correct, not as a skill silently missing from the form the user is
 * about to save. Private skills are rejected up front because the server
 * refuses them at save time anyway (`_validate_agent_available_skills`) —
 * catching it now costs one lookup instead of a failed save the model never
 * sees.
 *
 * Versions are deliberately left unpinned: the point of attaching a skill
 * rather than inlining its text is that edits to the skill take effect, and
 * the editor's own picker defaults to latest too.
 */
async function resolveSkills(
  names: string[],
  deps: ClientToolDeps,
): Promise<
  { ok: true; skills: AgentDraftSkill[] } | { ok: false; error: string }
> {
  if (names.length === 0) {
    return { ok: true, skills: [] };
  }
  if (!deps.skillService) {
    return {
      ok: false,
      error:
        "Cannot attach skills right now: the skill lookup service is " +
        "unavailable. Retry without the skills field.",
    };
  }
  const skills: AgentDraftSkill[] = [];
  for (const name of names) {
    try {
      const skill = await deps.skillService.getSkillByName(name, {
        orgId: deps.orgId,
      });
      if (skill.accessibility === "private") {
        return {
          ok: false,
          error:
            `The skill "${name}" is private, and only org-shared skills can ` +
            "be attached to an agent. Ask the user to share it with the org " +
            "first, or leave it off.",
        };
      }
      skills.push({ skill_id: skill.skill_id });
    } catch (error) {
      if (error instanceof RobotoNotFoundException) {
        return {
          ok: false,
          error:
            `No skill named "${name}" is visible in this org, so it can't be ` +
            "attached. Check the name against the skill registry.",
        };
      }
      return {
        ok: false,
        error:
          `Could not look up the skill named "${name}", so it is unknown ` +
          "whether it can be attached. Report the failure and let the user retry.",
      };
    }
  }
  return { ok: true, skills };
}

/**
 * Resolve the proposal's skill names onto `draft`, or return the `toolError`
 * to relay. Returns `null` when there is nothing to attach or it succeeded —
 * i.e. when the caller should carry on.
 *
 * Shared by both modes so create and update attach skills identically.
 */
async function attachSkillsToDraft(
  input: ClientCreateAgentInput,
  draft: AgentDraft,
  deps: ClientToolDeps,
): Promise<ClientToolExecutionResult | null> {
  const skillNames = normalizeSkillNames(input.skills);
  if (skillNames === undefined) {
    return null;
  }
  const resolved = await resolveSkills(skillNames, deps);
  if (!resolved.ok) {
    return toolError(resolved.error, { skills: skillNames });
  }
  draft.skills = resolved.skills;
  return null;
}

/**
 * The corrective half of a not-found message: what the model should do next,
 * given whatever we could learn about the org's agents.
 */
function describeAvailableAgents(available: string[] | null): string {
  if (available === null) {
    return "Check the name, or omit target_agent_name to create a new agent.";
  }
  if (available.length === 0) {
    return "This org has no agents yet — omit target_agent_name to create one.";
  }
  return (
    `Agents in this org: ${available.join(", ")}. Retry with the exact name, ` +
    "or omit target_agent_name to create a new agent."
  );
}

/** Cap on names listed back to the model after a failed lookup. */
const MAX_SUGGESTED_AGENT_NAMES = 30;

/**
 * Names of the org's agents, for a not-found message, or `null` if they
 * can't be listed.
 *
 * Best-effort by design: this runs while already reporting one failure, so a
 * second failure must degrade to a vaguer message rather than replace the
 * real error with a listing error.
 */
async function listAgentNames(deps: ClientToolDeps): Promise<string[] | null> {
  if (!deps.agentService || !deps.orgId) {
    return null;
  }
  try {
    const page = await deps.agentService.listAgents({
      orgId: deps.orgId,
      limit: MAX_SUGGESTED_AGENT_NAMES,
    });
    return page.items.map((agent) => agent.name);
  } catch {
    return null;
  }
}

async function executeUpdate(
  input: ClientCreateAgentInput,
  deps: ClientToolDeps,
  targetAgentName: string,
): Promise<ClientToolExecutionResult> {
  // Two distinct preconditions, reported separately: the model can act on a
  // missing org (ask the user to pick one) but not on a missing service.
  if (!deps.agentService) {
    return toolError(
      "Cannot update an agent by name right now: the agent lookup service " +
        "is unavailable.",
    );
  }
  if (!deps.orgId) {
    return toolError(
      "Cannot update an agent by name without an organization context. Ask " +
        "the user which org the agent belongs to.",
    );
  }
  // Name + org are jointly unique (`agents_unique_org_name`), so the
  // dedicated by-name route resolves the target in one indexed query — and
  // a miss (the common case when the model guesses a name) costs one 404
  // rather than a drain of every page of the org's agents.
  let agentId: string;
  try {
    const agent = await deps.agentService.getAgentByName(targetAgentName, {
      // The active org owns the agent being looked up, so it doubles as the
      // resource owner this route requires.
      resourceOwnerId: deps.orgId,
    });
    agentId = agent.agent_id;
  } catch (error) {
    // "No such name" and "the lookup failed" call for different responses, so
    // don't collapse them: telling the model a name is wrong when the request
    // actually 500'd invites it to retry as a create, which then collides
    // with `agents_unique_org_name`.
    if (error instanceof RobotoNotFoundException) {
      // Nothing lets the model enumerate agents, so a miss usually means it
      // guessed — in practice from a related skill's name. Naming the real
      // agents turns an otherwise dead end into a one-turn correction. Only
      // paid on failure, so it costs nothing on the normal path.
      const available = await listAgentNames(deps);
      return toolError(
        `No agent named "${targetAgentName}" exists in this org, so there is ` +
          `nothing to update. ${describeAvailableAgents(available)} Agent ` +
          "names are matched exactly, and are not the same as skill names.",
        { target_agent_name: targetAgentName },
      );
    }
    return toolError(
      `Could not look up the agent named "${targetAgentName}", so it is ` +
        "unknown whether it exists. Do not create a replacement; report the " +
        "failure and let the user retry.",
      { target_agent_name: targetAgentName },
    );
  }
  // Non-null in update mode by construction — `coerceToAgentDraft` only
  // returns null when a name is missing on a *create*. `?? {}` narrows the
  // type; a target with no other usable field is a valid "just open the
  // editor on this agent" request.
  const draft = coerceToAgentDraft(input) ?? {};
  const attached = await attachSkillsToDraft(input, draft, deps);
  if (attached !== null) {
    return attached;
  }
  draft.target_agent_id = agentId;
  stashAgentDraft(draft);
  const editRoute = Goto.createAgentRoute(agentId);
  deps.goto.navigateTo(editRoute);
  return toolSuccess("opened_agent_editor_for_update", {
    mode: "update",
    navigated_to: editRoute,
    target_agent_name: targetAgentName,
    agent_id: agentId,
  });
}

async function execute(
  input: ClientCreateAgentInput,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  if (!deps?.goto) {
    return toolError("Navigation is not available");
  }

  const targetAgentName = trimOrUndefined(input.target_agent_name);
  if (targetAgentName !== undefined) {
    return executeUpdate(input, deps, targetAgentName);
  }

  const draft = coerceToAgentDraft(input);
  if (draft === null) {
    return toolError("Agent draft requires a non-empty 'name'.");
  }
  const attached = await attachSkillsToDraft(input, draft, deps);
  if (attached !== null) {
    return attached;
  }
  stashAgentDraft(draft);
  deps.goto.navigateTo(Goto.route.CreateAgent);
  return toolSuccess("opened_agent_editor_with_draft", {
    mode: "create",
    navigated_to: Goto.route.CreateAgent,
    name: draft.name,
  });
}

// ---------------------------------------------------------------------------
// Tool module
// ---------------------------------------------------------------------------

export const tool = createClientTool<ClientCreateAgentInput>({
  name: "client_create_agent",
  description:
    "Open the Agent editor pre-filled with a drafted Agent template based on the current conversation. " +
    "Handles BOTH creating a new Agent and updating an existing one. " +
    "To CREATE: provide a 'name' and any description, model_profile, kickoff_message, goals, or variables you can infer. " +
    "To give the agent a stored procedure, attach the Skill by name via 'skills' — never paste a skill's body into kickoff_message, which forks the procedure and loses versioning. " +
    "To UPDATE an existing Agent, ALWAYS read it first: call get_entity on the Agent's name (or list_related with " +
    "related_type='agent' if you do not know the name) and work from the definition it returns. Its " +
    "'attached_skill_names' gives you the names of the currently attached Skills, which is what this tool takes. " +
    "Then pass the Agent's exact name as 'target_agent_name' along with the fields you are changing. " +
    "Anything you send REPLACES what is saved: goals, variables and skills each replace the whole list, so re-send the " +
    "entries you are keeping, copied VERBATIM from what you just read — never reworded, re-summarized or reordered — " +
    "alongside the ones you are adding. Omit a field entirely to leave it untouched, and omit 'name' to keep the saved " +
    "name or set it to rename. This is the only way to edit an Agent; there is no partial-update mode, which is exactly " +
    "why the read comes first — editing from memory silently drops whatever you did not recall. " +
    "If no Agent has that exact name, the tool returns an error listing the org's existing Agent names. " +
    "Use this when the user asks to turn the current agentThread (or a stated workflow) into a re-usable Agent, " +
    "to draft / create an Agent, or to change / improve an existing Agent. " +
    "In every case the user lands in the editor to review, refine, and save — this tool does NOT persist the Agent.",
  input_schema: inputSchema,
  execute,
  defaultApprovalMode: ApprovalMode.FOLLOW_AUTO,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Open agent editor with draft",
  displayDescription:
    "Opens the Agent editor pre-filled with a drafted Agent template. The agent isn't saved until you click Create in the editor.",
  ConfirmationContent: CreateAgentConfirmation,
});
